// The scout's conversation MEMORY (plan 101 doc 06 step 3) — the pure renderer
// that turns a conversation's prior `human_message` / `agent_response` run
// events into the digest one later session reads as context.
//
// THE ASK. "Talk to the scout about project-1 tomorrow" needs *something*, and
// the mailbox is deliberately ephemeral: it closes with the run and a later
// `message` gets `no_channel`. The cheapest honest answer is the one this file
// implements — the turns are ALREADY persisted as run events, so a later session
// on the same `conversation_ref` reads them back. That is compounding
// (`docs/idea/vision.md` § the design test) rather than a second session store.
//
// EXPLICITLY NOT A CHAT SERVICE. There is no conversation table, no message id,
// no thread, no delivery state and no read cursor. A conversation is a
// `conversation_ref` string a caller chose, and its transcript is a DERIVED read
// over `task_run_events`. Nothing here is a new store, and nothing here is a new
// public surface — the digest rides the assignment the mothership already builds
// (`FleetTaskAssignment.conversation_digest`, beside `kb_digest`), which is why
// the box needs no new capability to read it.
//
// RETENTION IS THE ONE-WAY DOOR, AND IT IS BORROWED, NOT INVENTED.
// `task_run_events.payload` is NULLed for `human_message` / `agent_response`
// after {@link RAW_LOG_RETENTION_HOURS} (#448, `redactStaleRunEventPayloads`) —
// so the deepest memory this feature can ever have is that window, and it is not
// this feature's to widen. Measured on prod 2026-08-30: 7,974 `agent_response`
// rows, 5,554 still carrying a payload — 2,420 already redacted. So the promise
// made to a user is exactly:
//
//   The scout recalls the turns of this conversation from the last 72 hours.
//   Older turns are COUNTED, never quoted.
//
// Counted, not dropped, is the load-bearing half: a conversation whose content
// was pruned must not read like a conversation that never happened. {@link
// renderConversationDigest} therefore renders a redacted turn as an explicit
// out-of-window line, so the model (and through it the human) can say "we spoke,
// I no longer have the words" instead of confabulating or claiming silence.
//
// WIDENING THE WINDOW IS A PRODUCT DECISION, NOT A CONSTANT EDIT. Longer
// retention is the BYO log sink (`/v1/billing/log-sink` — their DB, their
// retention), and a platform-side widening changes what every customer's raw
// logs cost us to hold. Neither is settled here.

/** Who spoke. `human` is a drained mailbox message, `agent` is the scout's answer. */
export type ConversationSpeaker = 'human' | 'agent';

/**
 * One recalled turn.
 *
 * `text: null` is the RETENTION case and not an error: the row is still there
 * (kind, order and timestamp are never pruned), only its payload was redacted at
 * 72h. Modelling it as a nullable field rather than dropping the row is what
 * makes "we spoke and I no longer hold the words" expressible — a discriminated
 * absence, in the repo's own idiom, instead of a silence that reads as never.
 */
export type ConversationTurn = {
  readonly speaker: ConversationSpeaker;
  /** The turn's prose, or null when the raw-log prune has already redacted it. */
  readonly text: string | null;
};

/**
 * Raw-log payload retention, in hours (#448).
 *
 * Defined HERE, in the package that imports nothing, because two consumers must
 * agree on it and they live in different apps: `apps/worker`'s prune cron
 * ENFORCES it, and this renderer STATES it to a model. A second copy would let
 * the promise drift away from the sweep that keeps it — the exact failure this
 * repo calls "a comment asserting a guarantee the code no longer provides".
 */
export const RAW_LOG_RETENTION_HOURS = 72;

/**
 * Turns carried into a later session, newest-last.
 *
 * A BOUND ON RECALL, never a token budget (BYOK — the tokens are the customer's,
 * `docs/standards/agent-budgets.md`). The failure it prevents is concrete: a
 * `conversation_ref` is a caller-chosen string with no lifetime, so a client that
 * reuses one forever accumulates turns without limit, and an unbounded prefix
 * would eventually crowd out the repo evidence the plan is actually made from.
 * The newest turns are kept because a conversation's tail is what the next
 * session is continuing.
 */
export const CONVERSATION_DIGEST_MAX_TURNS = 40;

/** Chars of one recalled turn. A RENDERING bound — the full turn stays on its own run event. */
export const CONVERSATION_DIGEST_TURN_MAX_CHARS = 600;

/**
 * Hard ceiling on the rendered digest, re-asserted by
 * `FleetTaskAssignmentSchema`. A backstop, not the mechanism: hitting it means
 * the turn/char bounds above are wrong. Sized under `KB_DIGEST_MAX_CHARS`
 * (20,000) — repo memory outranks chat recall when both ride one assignment.
 */
export const CONVERSATION_DIGEST_MAX_CHARS = 12_000;

/** What {@link renderConversationDigest} is handed. */
export type ConversationDigestInput = {
  /** The conversation this recalls — echoed into the digest so the model can name it. */
  readonly conversationRef: string;
  /** Prior turns in chronological order (oldest first). Only the newest {@link CONVERSATION_DIGEST_MAX_TURNS} are rendered. */
  readonly turns: readonly ConversationTurn[];
};

/** Prefix per speaker. Short, stable, and greppable in a prompt dump. */
const SPEAKER_LABEL: Record<ConversationSpeaker, string> = {
  human: 'Human',
  agent: 'Scout',
};

/** The line a redacted turn renders as — the promise's visible half. */
const OUT_OF_WINDOW = `(said, but past the ${RAW_LOG_RETENTION_HOURS}h retention window — content no longer held)`;

/** Collapse a turn's prose onto one line and bound it, so N turns render predictably. */
function renderTurnText(text: string): string {
  const flat = text.replaceAll(/\s+/g, ' ').trim();
  return flat.length > CONVERSATION_DIGEST_TURN_MAX_CHARS
    ? `${flat.slice(0, CONVERSATION_DIGEST_TURN_MAX_CHARS)}…`
    : flat;
}

/**
 * Render a conversation's prior turns as the digest an assignment carries, or
 * `null` when there is nothing to recall.
 *
 * `null` — not an empty string and not a header with no body — because an absent
 * field is what keeps the wire lean and what tells the box "this conversation has
 * no past", exactly as `kb_digest` does for a repo with no pages. A digest of
 * nothing but the header would make a first session and a fully-pruned one read
 * alike at the one seam that must tell them apart.
 *
 * A conversation whose every turn is out of window still renders: the counted
 * lines ARE the answer there.
 */
export function renderConversationDigest(input: ConversationDigestInput): string | null {
  const kept = input.turns.slice(-CONVERSATION_DIGEST_MAX_TURNS);
  if (kept.length === 0) return null;

  const dropped = input.turns.length - kept.length;
  const lines: string[] = [
    `Conversation ${input.conversationRef} — ${kept.length} earlier turn(s) recalled${
      dropped > 0 ? ` (${dropped} older turn(s) not shown)` : ''
    }.`,
  ];
  for (const turn of kept) {
    const body = turn.text === null ? OUT_OF_WINDOW : renderTurnText(turn.text);
    // A turn whose payload survived the prune but holds only whitespace is still
    // a turn that happened; render the out-of-window line rather than a bare
    // label, so no row ever renders as an empty utterance.
    lines.push(`- ${SPEAKER_LABEL[turn.speaker]}: ${body === '' ? OUT_OF_WINDOW : body}`);
  }

  const digest = lines.join('\n');
  return digest.length > CONVERSATION_DIGEST_MAX_CHARS
    ? digest.slice(0, CONVERSATION_DIGEST_MAX_CHARS)
    : digest;
}
